import { LinkedAppAuthStrategy } from '@nab/x-auth';
import { DafAuthToken, DafGrantType, DafLoginType } from '@nab/x-auth-core';
import { AppConfig } from '@nab/x-runtime';
import { XV2App } from '@nab/x-types';
import { constants, generateUniqueId, genIdentityAppTag, resolveIdentityAppTag } from '../../util';
import { CustomDafAuthConfig } from '../../interfaces';

/**
 * @constant successEventNames
 * Predefined values for the list of success Events names that is emitted from the identity miniapp.
 * This will help list the events to capture all data sent (including tokens) from identity miniapp via its success events
 */
const successEventNames: string[] = ['user_authenticated'];

/**
 * @constant failureEventNames
 * Predefined values for the list of failure event names that is emitted from the identity miniapp.
 * This will help list the events to capture all failure errors sent from identity miniapp.
 */
const failureEventNames: string[] = ['user_cancelled', 'user_login_failed'];
/**
 * @constant authAppMetaData
 * Predefined configuration required to load the identity miniapp
 */
const authAppMetaData: XV2App = {
    tag: 'nab-idp-usernamepassword',
    id: 'nab-idp-usernamepassword',
    appType: 'XV2',
    entryPoint: 'nab-idp-usernamepassword.js'
};

/**
 * @interface DafUsernamePwdIdentityMiniAppConfig
 * Interface to define the inputs required for the identity miniapp when loading or setting up the Auth strategy
 * This will be used by the Auth Strategy when it loads the identity miniapp and passes in this information in its `appConfig`
 */
export interface DafUsernamePwdIdentityMiniAppConfig extends AppConfig {
    clientId: string;
    tenant: string;
    environment: string;
    scope: string | string[];
    displayAsModal?: boolean;
    featureFlags?: {};
}

/**
 * @interface CustomDafUsernamePwdStrategyConfig
 * Interface that defines the inputs required to load the identity miniapps.
 * This should be provided when this Auth strategy is being configured.
 */
export interface CustomDafUsernamePwdStrategyConfig {
    assetManifestUrl: string;
    appConfig: DafUsernamePwdIdentityMiniAppConfig;
    onSuccess?: () => void;
    onFailure?: (e: any) => void;
    getHostElement: () => Promise<HTMLElement>;
}

/**
 */
export interface AuthTokenPayload {
    bearerToken: string;
    /* identity info is a special data that is sent from the Identity miniapp, that shell uses to save the username from */
    identityInfo: {
        username: string;
    };
    expires: number;
    idleTimeout: number;
}

/**
 * @class CustomDAFUsernamePwdStrategy
 * An auth strategy that loads the Identity miniapp to generate a token using user's credentials.
 */
export class CustomDAFUsernamePwdStrategy<AUTH extends CustomDafAuthConfig = CustomDafAuthConfig> extends LinkedAppAuthStrategy<
    XV2App,
    AUTH
> {
    constructor(config: CustomDafUsernamePwdStrategyConfig, protected isSeed: boolean = true) {
        /**
         * Setting the LinkedAppAuthStrategy to load the Identity miniapp and
         * listen to the events for failure and success.
         */
        super({
            linkedAppConfig: {
                appMetaData: {
                    ...authAppMetaData,
                    assetManifestUrl: config.assetManifestUrl
                },
                appConfig: {
                    ...config.appConfig,
                    isTokenCached: false,
                    showUserIdRetrievedScreen: true
                }
            },
            getHostElement: config.getHostElement,
            successEventNames,
            failureEventNames,
            onSuccess: config.onSuccess,
            onFailure: config.onFailure
        });
    }

    /**
     * This function determines if the authConfig, that is set on the runtime is valid to invoke this Strategy
     * @param config
     * @returns
     */
    public supports(config: AUTH) {
        return (
            config.tokenSource === 'DAF' &&
            config.grantType === DafGrantType.UsernamePassword && // Update the supports to be have a specific grant type instead of anonymous grant type
            config.flags?.loginType === DafLoginType.USER_LOGIN &&
            (config.tokenType === 'Bearer' || config.tokenType === 'JWT') &&
            !!config.isSeed === !!this.isSeed
        );
    }
    /**
     *This function accept the auth config and passed into the super class to generate the token,expires and idleTimeout
     * @param config
     * @returns
     */
    public async authenticate<T = DafAuthToken<AUTH>>(config: AUTH): Promise<T> {
        const designSystem = config?.designSystem;
        const tag = resolveIdentityAppTag(constants.IDMA_USERNAMEPASSWORD_TAG, designSystem);
        const { appMetaData } = genIdentityAppTag(tag);
        // @ts-ignore - initConfig is protected in parent strategy implementation
        this.initConfig.linkedAppConfig.appMetaData = {
            // @ts-ignore - initConfig is protected in parent strategy implementation
            ...this.initConfig.linkedAppConfig.appMetaData,
            ...appMetaData,
        };

        const { bearerToken, identityInfo } = await super.authenticate<AuthTokenPayload>(config); // the data sent from Identity mioniapp does not match teh AuthToken Interface
        const token = {
            id: generateUniqueId(),
            tokenValue: bearerToken,
            identityInfo,
            expires: Date.now() + 15 * 60 * 60 * 1000, // setting default to 15 mins as identity miniapp does return the expiry
            idleTimeout: Date.now() + 5 * 60 * 60 * 1000, // setting default to 15 mins as identity miniapp does return the expiry
            config: {
                ...config,
                flags: null
            }
        };

        return token as unknown as T;
    }
}